import Link from "next/link";
import React from "react";
import SiteShell, { APP_URL } from "@/components/SiteShell";
import JsonLd from "@/components/JsonLd";
import { SITE_URL, definedTermSetLd } from "@/lib/structuredData";
import { GLOSSARY, termsByCategory } from "@/lib/glossary";

const CATEGORY_COLORS: Record<string, string> = {
  "Funnel strategy": "bg-amber-50 text-amber-700 border-amber-200",
  "Automation mechanics": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Contacts & data": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Email & SMS delivery": "bg-sky-50 text-sky-700 border-sky-200",
};

function anchorFor(category: string) {
  return category.toLowerCase().replace(/[^a-z0-9]+/g, "-");
}

/** Glossary hub (www.frazzlecrm.com/learn) — indexable. */
export default function LearnIndex() {
  const groups = termsByCategory();

  return (
    <SiteShell
      title="CRM & marketing automation glossary — FrazzleCRM"
      description={`Plain-English definitions of the ${GLOSSARY.length} CRM and marketing-automation terms that decide whether a funnel works: triggers, enrollment, waits, goals, stop tags, send windows, trigger links, merge fields, DKIM, 10DLC and more.`}
      canonical={`${SITE_URL}/learn`}
      ogTitle="The CRM automation glossary, without the marketing fluff"
    >
      <JsonLd
        data={definedTermSetLd(
          GLOSSARY.map(({ slug, term, definition }) => ({ slug, term, definition }))
        )}
      />

      <section className="bg-gradient-to-b from-indigo-50/60 to-white">
        <div className="mx-auto max-w-4xl px-6 pb-10 pt-14 sm:pt-16">
          <div className="mb-4 w-fit rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-[12px] font-semibold text-indigo-700">
            Glossary · {GLOSSARY.length} terms
          </div>
          <h1 className="text-[32px] font-extrabold leading-[1.15] tracking-tight text-slate-900 sm:text-[42px]">
            The words that decide whether your funnel works
          </h1>
          <p className="mt-4 max-w-2xl text-[15.5px] leading-relaxed text-slate-600">
            Every one of these is a place we have watched a funnel break: a
            branch with nothing on the far side, a reminder that fired after the
            appointment, a stop tag a flow applied to itself. Definitions first,
            then how the thing actually works, then the mistake people make.
          </p>
          <p className="mt-3 max-w-2xl text-[14px] leading-relaxed text-slate-500">
            Where FrazzleCRM has no feature for something, these pages say so
            and show what it is built from instead.
          </p>
        </div>
      </section>

      {/* Jump links — a 24-term list needs a table of contents. */}
      <nav
        aria-label="Glossary categories"
        /* Sticky only from sm up: at 375px four chips stack into four rows
           and a sticky bar would eat a quarter of the viewport for the whole
           scroll. */
        className="z-10 border-b border-slate-100 bg-white/90 backdrop-blur sm:sticky sm:top-0"
      >
        <div className="mx-auto flex max-w-4xl flex-wrap gap-2 px-6 py-3">
          {groups.map((g) => (
            <a
              key={g.category}
              href={`#${anchorFor(g.category)}`}
              className={`inline-flex min-h-[44px] items-center rounded-full border px-4 text-[13px] font-semibold ${
                CATEGORY_COLORS[g.category] ??
                "border-slate-200 bg-slate-50 text-slate-600"
              }`}
            >
              {g.category}
              <span className="ml-1.5 opacity-60">{g.items.length}</span>
            </a>
          ))}
        </div>
      </nav>

      <section className="mx-auto max-w-4xl px-6 pb-16 pt-10">
        {groups.map((g) => (
          <div key={g.category} id={anchorFor(g.category)} className="mb-12 scroll-mt-20">
            <h2 className="text-[22px] font-bold tracking-tight text-slate-900">
              {g.category}
            </h2>
            <p className="mt-1 text-[14px] text-slate-500">{g.blurb}</p>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {g.items.map((t) => (
                <Link
                  key={t.slug}
                  href={`/learn/${t.slug}`}
                  className="group flex flex-col rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
                >
                  <h3 className="text-[16.5px] font-bold text-slate-900 group-hover:text-indigo-700">
                    {t.term}
                  </h3>
                  {t.aka && (
                    <p className="mt-1 text-[12px] text-slate-400">
                      also: {t.aka.join(", ")}
                    </p>
                  )}
                  <p className="mt-2 flex-1 text-[13.5px] leading-relaxed text-slate-600">
                    {t.definition}
                  </p>
                  <span className="mt-3 text-[13px] font-semibold text-indigo-600">
                    Read the definition <span aria-hidden>→</span>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        ))}

        <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-8 text-center">
          <h2 className="text-[22px] font-bold text-slate-900">
            Definitions are cheaper than diagrams
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-[14px] leading-relaxed text-slate-600">
            Every term here shows up as a real node on a real map. Browse the{" "}
            <Link
              href="/templates"
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              free funnel templates
            </Link>{" "}
            to see them wired together, or describe your business to the{" "}
            <Link
              href="/tools/funnel-generator"
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              funnel map generator
            </Link>{" "}
            and get a map of your own.
          </p>
          <a
            href={`${APP_URL}/signup`}
            className="mt-5 inline-block rounded-xl bg-indigo-600 px-6 py-3 text-[14px] font-semibold text-white shadow-lg shadow-indigo-200 hover:bg-indigo-500"
          >
            Start free — no credit card
          </a>
        </div>
      </section>
    </SiteShell>
  );
}
